import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ParsedFileData } from '../models/file-upload.model';
import { UploadStatus } from '../enums/upload-status.enum';

// CP file record type identifiers (fixed-width text format)
const RECORD_01 = '01'; // Header
const RECORD_02 = '02'; // First detail record (country info)
const RECORD_99 = '99'; // Trailer (counts & amounts)

// Record 01 positions (0-indexed)
const SLA_FLAG_POS = 348;
const SLA_FEE_WAIVED_POS = 349;

// Record 99 positions (0-indexed)
// USA
const USA_DOCS_START = 43;
const USA_DOCS_END = 48;
const USA_AMOUNT_START = 48;
const USA_AMOUNT_END = 60;
// CAN
const CAN_DOCS_START = 54;
const CAN_DOCS_END = 64;
const CAN_AMOUNT_START = 64;
const CAN_AMOUNT_END = 76;
// Both (explicit check/DDA counts in trailer)
const CHECKS_START = 117;
const CHECKS_END = 127;
const DDAS_START = 127;
const DDAS_END = 137;

// Record 02 positions for file-type fallback detection (0-indexed)
// When trailer has no explicit check/DDA split, inspect the last record 02:
// presence of digits in routing/account/check-number fields → CHECK file; otherwise DDA
const ROUTING_NUMBER_START = 671;
const ROUTING_NUMBER_END = 691;
const ACCOUNT_NUMBER_START = 691;
const ACCOUNT_NUMBER_END = 711;
const CHECK_NUMBER_START = 713;
const CHECK_NUMBER_END = 723;

// CAN language codes embedded in record 02, positions 2-3
const CAN_LANG_CODES = ['FR', 'EN'];

@Injectable({ providedIn: 'root' })
export class CpFileParserService {
  /**
   * Parse a CP (Check Print) fixed-width text file in the browser.
   * Reads as ASCII, extracts metadata from 01/02/99 records.
   */
  parseFile(file: File, fileId: string): Observable<ParsedFileData> {
    return new Observable(observer => {
      const reader = new FileReader();

      reader.onload = e => {
        try {
          const text = e.target?.result as string;
          const result = this.extract(text, fileId, file.name);
          observer.next(result);
          observer.complete();
        } catch (err) {
          observer.error(
            new Error(`Parse error for ${file.name}: ${(err as Error).message}`)
          );
        }
      };

      reader.onerror = () => {
        observer.error(new Error(`Could not read file: ${file.name}`));
      };

      // CP files are ASCII — must not use UTF-8 or char positions shift
      reader.readAsText(file, 'ascii');
    });
  }

  private extract(
    text: string,
    fileId: string,
    fileName: string
  ): ParsedFileData {
    const lines = text.split('\n');

    const record01 = lines.find(l => l.startsWith(RECORD_01)) ?? '';
    const record02 = lines.find(l => l.startsWith(RECORD_02)) ?? '';
    // Last line starting with 99 is the trailer
    const record99 =
      [...lines].reverse().find(l => l.startsWith(RECORD_99)) ?? '';

    if (!record01) {
      return this.invalid(
        fileId,
        fileName,
        'Invalid file: header record (01) not found'
      );
    }
    if (!record99) {
      return this.invalid(
        fileId,
        fileName,
        'Invalid file: trailer record (99) not found'
      );
    }

    // Country: derived from language code in first detail record (02), positions 2-3
    const langCode = record02.substring(2, 4).toUpperCase();
    const countryCode = CAN_LANG_CODES.includes(langCode) ? 'CAN' : 'USA';

    // SLA flags from header record
    const sla = record01.charAt(SLA_FLAG_POS) === 'Y';
    const isSlaFeeWaived = record01.charAt(SLA_FEE_WAIVED_POS) === 'Y';

    // Document count and total amount — positions differ by country
    let rawDocCount: number;
    let totalAmount: number;

    if (countryCode === 'USA') {
      rawDocCount = this.parseNum(
        record99.substring(USA_DOCS_START, USA_DOCS_END)
      );
      totalAmount = this.parseAmount(
        record99.substring(USA_AMOUNT_START, USA_AMOUNT_END)
      );
      // USA doc count encodes header + footer lines: actual docs = (rawCount - 2) / 6
      const docsInFile =
        rawDocCount > 2 ? Math.floor((rawDocCount - 2) / 6) : 0;
      const { checkCount, ddaCount } = this.resolveCheckDdaCounts(
        record99,
        record02,
        docsInFile
      );

      return this.valid(
        fileId,
        fileName,
        countryCode,
        docsInFile,
        { checkCount, ddaCount, totalAmount },
        sla,
        isSlaFeeWaived
      );
    } else {
      rawDocCount = this.parseNum(
        record99.substring(CAN_DOCS_START, CAN_DOCS_END)
      );
      totalAmount = this.parseAmount(
        record99.substring(CAN_AMOUNT_START, CAN_AMOUNT_END)
      );
      const { checkCount, ddaCount } = this.resolveCheckDdaCounts(
        record99,
        record02,
        rawDocCount
      );

      return this.valid(
        fileId,
        fileName,
        countryCode,
        rawDocCount,
        { checkCount, ddaCount, totalAmount },
        sla,
        isSlaFeeWaived
      );
    }
  }

  /**
   * Resolve check/DDA split using the same two-path logic as fps-cp:
   * Path 1 — trailer positions 117-136 have explicit counts → use them.
   * Path 2 — those positions are blank → infer file type from record 02:
   *   routing/account/check-number fields containing digits → CHECK file;
   *   otherwise DDA file.  All docs go to the detected type.
   */
  private resolveCheckDdaCounts(
    record99: string,
    record02: string,
    docsInFile: number
  ): { checkCount: number; ddaCount: number } {
    const hasExplicit =
      record99.length > DDAS_START &&
      (record99.substring(CHECKS_START, CHECKS_END).trim() !== '' ||
        record99.substring(DDAS_START, DDAS_END).trim() !== '');

    if (hasExplicit) {
      return {
        checkCount: this.parseNum(record99.substring(CHECKS_START, CHECKS_END)),
        ddaCount: this.parseNum(record99.substring(DDAS_START, DDAS_END)),
      };
    }

    // Fallback: determine file type from the last record 02
    const isCheckFile = this.isCheckFileType(record02);
    return isCheckFile
      ? { checkCount: docsInFile, ddaCount: 0 }
      : { checkCount: 0, ddaCount: docsInFile };
  }

  /**
   * Mirrors fps-cp GetFileType: if routing number (671-691), account number (691-711),
   * or check number (713-723) in record 02 contain any digit → CHECK; otherwise DDA.
   */
  private isCheckFileType(record02: string): boolean {
    if (!record02 || record02.length < CHECK_NUMBER_END) {
      return true; // default to CHECK if record is too short to inspect
    }
    const hasDigit = (s: string) => /\d/.test(s);
    return (
      hasDigit(record02.substring(ROUTING_NUMBER_START, ROUTING_NUMBER_END)) ||
      hasDigit(record02.substring(ACCOUNT_NUMBER_START, ACCOUNT_NUMBER_END)) ||
      hasDigit(record02.substring(CHECK_NUMBER_START, CHECK_NUMBER_END))
    );
  }

  private valid(
    fileId: string,
    fileName: string,
    countryCode: string,
    docsInFile: number,
    counts: { checkCount: number; ddaCount: number; totalAmount: number },
    sla: boolean,
    isSlaFeeWaived: boolean
  ): ParsedFileData {
    return {
      fileId,
      fileName,
      originalFileName: fileName,
      countryCode,
      country: countryCode === 'CAN' ? 'Canada' : 'United States',
      uploadStatus: UploadStatus.Pending,
      uploadProgress: 0,
      docsInFile,
      checkCount: counts.checkCount,
      ddaCount: counts.ddaCount,
      totalAmount: counts.totalAmount,
      sla,
      isSlaFeeWaived,
      isValid: true,
    };
  }

  private invalid(
    fileId: string,
    fileName: string,
    message: string
  ): ParsedFileData {
    return {
      fileId,
      fileName,
      originalFileName: fileName,
      countryCode: '',
      country: '',
      uploadStatus: UploadStatus.Failed,
      uploadProgress: 0,
      docsInFile: 0,
      checkCount: 0,
      ddaCount: 0,
      totalAmount: 0,
      sla: false,
      isSlaFeeWaived: false,
      isValid: false,
      validationMessage: message,
    };
  }

  private parseNum(raw: string): number {
    const n = parseInt(raw.trim(), 10);
    return isNaN(n) ? 0 : Math.max(0, n);
  }

  private parseAmount(raw: string): number {
    const n = parseFloat(raw.trim());
    return isNaN(n) ? 0 : Math.max(0, n);
  }
}
